import React, { useEffect, useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import axios from 'axios';
import { TextField, Paper, Grid, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

const TeaAndFactoryCollection = () => {
  const [collectData, setCollectData] = useState([]);
  const [factoryData, setFactoryData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const formattedDate = format(selectedDate, 'yyyy-MM-dd');

  const parseCustomDate = (dateStr) => {
    if (!dateStr) return null;
    const parsed1 = new Date(dateStr);
    if (!isNaN(parsed1)) return parsed1;

    const parsed2 = parse(dateStr, 'dd/MM/yy', new Date());
    return isValid(parsed2) ? parsed2 : null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collectRes, factoryRes] = await Promise.all([
          axios.get('https://kmtea.onrender.com/collecttea'),
          axios.get('https://kmtea.onrender.com/factory'),
        ]);
        setCollectData(collectRes.data || []);
        setFactoryData(factoryRes.data || []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, []);

  const filteredCollectData = collectData.filter((row) => {
    const parsedDate = parseCustomDate(row.date);
    return parsedDate && format(parsedDate, 'yyyy-MM-dd') === formattedDate;
  });

  const filteredFactoryData = factoryData.filter((row) => {
    const parsedDate = parseCustomDate(row.date);
    return parsedDate && format(parsedDate, 'yyyy-MM-dd') === formattedDate;
  });

const collectColumns = [
  { field: 'name', headerName: 'Name', width: 120 },
  { field: 'area', headerName: 'Area', width: 120 },
  { field: 'qty', headerName: 'Qty', width: 80 },
  { field: 'empid', headerName: 'Emp ID', width: 100 },
  { field: 'farmid', headerName: 'Farm ID', width: 100 },
  { field: 'tare', headerName: 'Tare', width: 80 },
  { field: 'netweight', headerName: 'Net Weight', width: 100 },
  { field: 'time', headerName: 'Time', width: 100 },
  {
    field: 'qtyList',
    headerName: 'Qty List',
    width: 150,
    renderCell: (params) => {
      const value = params.value;
      try {
        const list = typeof value === 'string' ? JSON.parse(value) : value;
        return Array.isArray(list) ? list.join(', ') : '';
      } catch (e) {
        return value || '';
      }
    }
  }
];


  const factoryColumns = [
    { field: 'empid', headerName: 'Emp ID', width: 100 },
    { field: 'time', headerName: 'Time', width: 100 },
    { field: 'quantity', headerName: 'Quantity', width: 100 },
    { field: 'bagqty', headerName: 'Bag Qty', width: 100 },
    { field: 'description', headerName: 'Description', width: 150 },
    { field: 'netweight', headerName: 'Net Weight', width: 100 },
    { field: 'totalamount', headerName: 'Total Amount', width: 120 },
    { field: 'price', headerName: 'Price', width: 100 },
    { field: 'factoryname', headerName: 'Factory Name', width: 150 },
    { field: 'tc', headerName: 'TC', width: 100 },
  ];

  const totalCollectQty = filteredCollectData.reduce((sum, row) => sum + (row.qty || 0), 0);
  const totalCollectWeight = filteredCollectData.reduce((sum, row) => sum + (row.netweight || 0), 0);
  const totalCollectBags = filteredCollectData.reduce((total, row) => {
    try {
      const qtyList = typeof row.qtyList === 'string'
        ? JSON.parse(row.qtyList)
        : row.qtyList;
      return total + (Array.isArray(qtyList) ? qtyList.length : 0);
    } catch {
      return total;
    }
  }, 0);

  const totalFactoryQty = filteredFactoryData.reduce((sum, row) => sum + (row.quantity || 0), 0);
  const totalFactoryWeight = filteredFactoryData.reduce((sum, row) => sum + (row.netweight || 0), 0);
  const totalFactoryBags = filteredFactoryData.reduce((sum, row) => sum + (row.bagqty || 0), 0);

  return (
    <div style={{ padding: 16 }}>
      <TextField
        label="Select Date"
        type="date"
        value={formattedDate}
        onChange={(e) => setSelectedDate(new Date(e.target.value))}
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2 }}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={4} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Tea Collection - {formattedDate}
            </Typography>
            <DataGrid
              rows={filteredCollectData.map((row, i) => ({ id: i + 1, ...row }))}
              columns={collectColumns}
              pageSize={5}
              autoHeight
              density="compact"
            />
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Total Qty: {totalCollectQty} &nbsp;|&nbsp;
              Total Net Weight: {totalCollectWeight} &nbsp;|&nbsp; <br></br>
              <strong>No. of Bags: {totalCollectBags}</strong>
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={4} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Factory Collection - {formattedDate}
            </Typography>
            <DataGrid
              rows={filteredFactoryData.map((row, i) => ({ id: i + 1, ...row }))}
              columns={factoryColumns}
              pageSize={5}
              autoHeight
              density="compact"
            />
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Total Qty: {totalFactoryQty} &nbsp;|&nbsp;
              Total Net Weight: {totalFactoryWeight} &nbsp;|&nbsp; <br></br>
              <strong>No. of Bags: {totalFactoryBags}</strong>
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default TeaAndFactoryCollection;
